import "./style.scss";

import ActionCopy from "assets/img/svg/actionCopy";
import Plus from "assets/img/svg/plus";
import Supply from "assets/img/svg/supply";
import { getAccountingTypologyName } from "components/Beneficiar/utils";
import ModalBox from "components/Modal";
import DeleteModal from "components/shared/DeleteModal";
import InsertVariable from "components/shared/InsertVariableMenu";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { IBrand } from "store/brandStore";
import type { ICompany } from "store/settingsStore";
import type { AccountingEntry, AccountingMapping } from "store/types";
import { AccountingAccountCategories, AccountingExportMapKeys } from "store/types";
import { v4 as uuidv4 } from "uuid";

import AccountingMappingTable from "../AccountingMappingTable";
import AccountingComparisonTable from "../ComparisonTable";
import { AddFilter } from "../ModalContent/AddFilter";
import type { AccountingFilterOptions, AccountingTypologyFilter } from "../ModalContent/AddFilter/useAccountingFilters";
import { useAppliedFilter } from "../ModalContent/AddFilter/useAccountingFilters";
import DuplicateEntry from "../ModalContent/DuplicateEntry";

interface IAccountingTypology {
    propEntry: AccountingEntry;
    onDeleteEntry: (data: AccountingEntry) => Promise<void>;
    onChange: (data: AccountingEntry) => void;
    companyDetails: {
        usesAccountingWithChannelDispatch: boolean;
        usesBrands: boolean;
        companyInfos: ICompany | undefined;
    };
    isDefaultTypology: boolean;
    brands: IBrand[];
    entriesNames: string[];
    exportType: AccountingMapping["exportType"];
    accountingGroupMethod: "beneficiary" | "term" | "typology";
    filters: AccountingTypologyFilter[];
    filterOptions: AccountingFilterOptions;
}

const LabelVars = ["{{name}}", "{{firstname}}", "{{year}}", "{{periodStart}}", "{{periodEnd}}", "{{title}}"] as const;

const AccountingTypology = (props: IAccountingTypology) => {
    const {
        propEntry,
        exportType,
        onDeleteEntry,
        onChange,
        companyDetails,
        isDefaultTypology,
        entriesNames,
        accountingGroupMethod,
        filters,
        filterOptions,
    } = props;
    const { usesAccountingWithChannelDispatch: usesFilters } = companyDetails;
    const [t] = useTranslation();

    const isExpensesExportType = exportType === "advanceReduction" || exportType === "allowance";

    const [entry, setEntry] = useState<AccountingEntry>(propEntry);

    const [mapping, setMapping] = useState<AccountingMapping[]>(
        [...(propEntry.mapping ?? [])]
            .sort((a, b) => a.appearanceOrder - b.appearanceOrder)
            .map((line) => {
                if (line.id) {
                    return { ...line, key: line.id };
                } else {
                    return { ...line, key: uuidv4() };
                }
            }),
    );

    const [cursorPosition, setCursorPosition] = useState(entry.note.length);

    const [modalType, setModalType] = useState("");
    const [modalData, setModalData] = useState<AccountingMapping | AccountingEntry>();

    const exportTypeLineFilter = useCallback((line: AccountingMapping) => line.exportType === exportType, [exportType]);

    useEffect(() => {
        setEntry(propEntry);
    }, [propEntry]);

    const textFieldRef = useRef<HTMLInputElement>(null);

    const variables = useMemo<Partial<Record<(typeof LabelVars)[number], string>>>(() => {
        const vars = {
            "{{name}}": t("Accounting.VariableName"),
            "{{firstname}}": t("Accounting.VariableFirstname"),
            "{{year}}": t("Accounting.VariableYear"),
            "{{periodStart}}": t("Accounting.PeriodStart"),
            "{{periodEnd}}": t("Accounting.PeriodEnd"),
        };

        if (accountingGroupMethod === "term") {
            vars["{{title}}"] = t("Accounting.VariableTitleName");
        }

        return vars;
    }, [t, accountingGroupMethod]);

    const { appliedFilter, filterName, filterOptionName } = useAppliedFilter(
        entry.filters,
        filters,
        filterOptions,
        usesFilters,
    );

    const handleFilterTypeValueChange = <K extends keyof AccountingEntry>(key: K, value: any) => {
        setEntry({
            ...entry,
            [key]: value,
        });

        onChange({
            ...entry,
            [key]: value,
            mapping,
        });
    };

    const handleMappingValueChange = <K extends keyof AccountingMapping>(key: K, index: number, value: any) => {
        const temp_mapping = mapping.map((line, i) => {
            if (i === index) {
                return {
                    ...line,
                    [key]: value,
                };
            }

            return line;
        });

        setMapping(temp_mapping);

        onChange({
            ...entry,
            mapping: temp_mapping,
        });
    };

    const handleEntryDeletion = async () => {
        if (!isDefaultTypology) {
            await onDeleteEntry({ ...entry });
        }
    };

    const handleSelectClick = (cursorPosition: number) => {
        if (textFieldRef.current) {
            setCursorPosition(cursorPosition);
        }
    };

    const handleSelectVariable = (newValue: string) => {
        setEntry({
            ...entry,
            note: newValue,
        });

        setCursorPosition(newValue.length);

        onChange({
            ...entry,
            note: newValue,
        });
    };

    const handleAddMapping = () => {
        const temp_mapping: AccountingMapping[] = [
            ...mapping,
            {
                accountType: "distributorContributions",
                accountNumber: "",
                direction: "debit",
                appearanceOrder: mapping.length,
                key: uuidv4(),
                exportType: exportType,
                accountCategory: mapping[0]?.accountCategory ?? "X",
                attachAuxiliaryOnExport: 0,
                journalCode: mapping[mapping.length - 1]?.journalCode,
            },
        ];

        setMapping(temp_mapping);
    };

    const handleMappingDeletion = (line: AccountingMapping, appearanceOrder: number) => {
        const temp_mapping = mapping.filter((_mapping) => _mapping.key !== line.key);
        changeAppearanceOrder(temp_mapping, appearanceOrder);
    };

    const changeAppearanceOrder = (mapping: AccountingMapping[], order: number) => {
        const temp_mapping = mapping.map((line, i) => {
            if (line.appearanceOrder !== i) {
                return {
                    ...line,
                    appearanceOrder: i,
                };
            } else {
                return line;
            }
        });

        setMapping(temp_mapping);

        onChange({
            ...entry,
            mapping: temp_mapping,
        });
    };

    const handleOpeningDeletModal = () => {
        const hasParentTypology = entriesNames.filter((item) => entry.name === item).length > 1;

        if (!hasParentTypology || !!appliedFilter) {
            setModalType("delete-entry");
            setModalData(entry);
        } else {
            toast.error(`${t("Accounting.Filters.DeleteError")}`, { autoClose: 5000 });
        }
    };

    const accountingValues = useMemo(() => {
        return AccountingExportMapKeys[exportType] || AccountingExportMapKeys.default;
    }, [exportType]);

    return (
        <>
            <DeleteModal
                onClose={() => setModalType("")}
                open={modalType === "delete-entry"}
                onDelete={handleEntryDeletion}
                elementTitle={t("DeleteModal.TheTypology", { name: entry.name })}
                description={t("Accounting.ModalDeleteEntry.BeneficiariesFallback")}
            />
            <ModalBox
                onClose={() => setModalType("")}
                open={modalType === "copy-entry"}
                mainTitle={t("Accounting.ModalDuplicateEntry.Title")}
            >
                <DuplicateEntry entry={modalData as AccountingEntry} close={() => setModalType("")} />
            </ModalBox>
            <ModalBox
                onClose={() => setModalType("")}
                open={modalType === "add-filter"}
                mainTitle={t("Accounting.Filters.AddFilter")}
            >
                <AddFilter
                    close={() => setModalType("")}
                    entry={modalData as AccountingEntry}
                    filters={filters}
                    filterOptions={filterOptions}
                />
            </ModalBox>
            <div className="accounting-settings-typologies" id={`typology-${entry.id}`}>
                <div className="accounting-typologies-top">
                    <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                        <h3 className="information-title">
                            {t("Accounting.Typology")}: {getAccountingTypologyName(entry.name)}{" "}
                            {!!appliedFilter &&
                                t("Accounting.Filters.TypologyFilter", {
                                    filterName: t(filterName),
                                    filterOptionName: t(filterOptionName),
                                })}
                        </h3>
                        {!appliedFilter && (
                            <span className="information-beneficiaries">
                                {entry.matchingBeneficiaries || 0} {t("Accounting.Beneficiaries")}
                            </span>
                        )}
                    </div>
                    <div className={`accounting-typologies-actions ${usesFilters ? "column" : ""}`}>
                        <div className={`${!isDefaultTypology ? "top" : ""}`} style={{ width: "100%" }}>
                            <button
                                className={`action ${entry.name !== "Par défaut" ? "small" : "large"}`}
                                type="button"
                                onClick={() => {
                                    setModalType("copy-entry");
                                    setModalData(entry);
                                }}
                            >
                                <ActionCopy />
                                {t("Accounting.Duplicate")}
                            </button>

                            {!isDefaultTypology && (
                                <button
                                    className="action delete"
                                    type="button"
                                    onClick={() => {
                                        handleOpeningDeletModal();
                                    }}
                                >
                                    <Supply />
                                    <span>{t("Accounting.Delete")}</span>
                                </button>
                            )}
                        </div>
                        {usesFilters && !appliedFilter && (
                            <button
                                type="button"
                                className="action large"
                                onClick={() => {
                                    setModalType("add-filter");
                                    setModalData(entry);
                                }}
                            >
                                <Plus />
                                {t("Accounting.Filters.AddFilter")}
                            </button>
                        )}
                    </div>
                </div>
                <div className="accounting-typologies-info">
                    <div className="typology-description">
                        <div className="typology-description-top">
                            <h5 className="typology-info-title">{t("Accounting.EntryTypeNote")}</h5>
                            <InsertVariable
                                cursorPosition={cursorPosition}
                                field={entry.note}
                                variables={variables}
                                // description={descriptionVariables}
                                onSelect={handleSelectVariable}
                            />
                        </div>
                        <StyledTextField
                            // size="small"
                            className="typology-description-input"
                            placeholder={t("Accounting.EntryTypeNote")}
                            value={entry.note}
                            trueInputRef={textFieldRef}
                            onChange={(e) => handleFilterTypeValueChange("note", e.target.value)}
                            onClick={() =>
                                handleSelectClick(
                                    textFieldRef.current
                                        ? (textFieldRef.current.selectionStart as number)
                                        : entry.note.length,
                                )
                            }
                            onKeyUp={() =>
                                handleSelectClick(
                                    textFieldRef.current
                                        ? (textFieldRef.current.selectionStart as number)
                                        : entry.note.length,
                                )
                            }
                        />
                    </div>

                    <div className="typology-description" style={{ marginTop: "1.5rem" }}>
                        <div className="typology-description-top">
                            <h5 className="typology-info-title">{t("Accounting.EntryAnalysisAxis")}</h5>
                        </div>
                        <StyledTextField
                            size="small"
                            className="typology-description-input"
                            placeholder={t("Accounting.EntryAnalysisAxisNote")}
                            value={entry.analysisAxis}
                            trueInputRef={textFieldRef}
                            onChange={(e) => handleFilterTypeValueChange("analysisAxis", e.target.value)}
                        />
                    </div>

                    <div className="account-typologies-entries">
                        <h5 className="typology-info-title">{t("Accounting.Entries")}</h5>
                        <p className="typology-info-text">{t("Accounting.EntriesText")}</p>
                        {isExpensesExportType ? null : (
                            <AccountingComparisonTable entry={entry} mapping={mapping.filter(exportTypeLineFilter)} />
                        )}
                        <AccountingMappingTable
                            mapping={mapping}
                            AccountingAccountCategories={AccountingAccountCategories}
                            handleMappingDeletion={handleMappingDeletion}
                            handleMappingValueChange={handleMappingValueChange}
                            filterMapping={exportTypeLineFilter}
                            valueTypeList={accountingValues.map((type) => ({
                                id: type,
                                label: t(`Accounting.Form.${type}`),
                            }))}
                        />
                        <button className="accounting-add-entry" onClick={() => handleAddMapping()}>
                            <Plus />
                            {t("Accounting.AddEntry")}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default memo(AccountingTypology);
